/**
 * Client-side field condition evaluation.
 *
 * Single source of truth for the admin form + repeater layouts. Mirrors the PHP
 * reference implementation (Modules\Fields\Definitions\ConditionalLogic) — parity
 * is guarded by shared fixtures in src/Fields/Tests/fixtures/conditions/.
 */

export interface ConditionRule {
    field?: string;
    key?: string;
    comparison?: string;
    value?: unknown;
}

export interface ConditionalLogic {
    action?: 'show' | 'hide';
    logic?: 'all' | 'any';
    rules?: ConditionRule[];
}

type FormData = Record<string, unknown>;

/** PHP empty() semantics: null, undefined, '', '0', 0, false, []. */
const phpEmpty = (value: unknown): boolean => {
    if (value === null || value === undefined || value === false || value === '' || value === '0') {
        return true;
    }

    if (typeof value === 'number') {
        return value === 0;
    }

    return Array.isArray(value) && value.length === 0;
};

const isNumeric = (value: unknown): boolean =>
    typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)));

/** PHP 8 loose comparison (==) for the value shapes form fields produce. */
const phpLooseEquals = (a: unknown, b: unknown): boolean => {
    const left = a === undefined ? null : a;
    const right = b === undefined ? null : b;

    if (left === null && right === null) {
        return true;
    }

    // bool <=> anything: the other side is cast to bool
    if (typeof left === 'boolean' || typeof right === 'boolean') {
        return !phpEmpty(left) === !phpEmpty(right);
    }

    // null <=> string compares as '' <=> string; null <=> number as 0 <=> number
    if (left === null) {
        return typeof right === 'string' ? right === '' : Number(right) === 0;
    }

    if (right === null) {
        return typeof left === 'string' ? left === '' : Number(left) === 0;
    }

    if (isNumeric(left) && isNumeric(right)) {
        return Number(left) === Number(right);
    }

    return String(left) === String(right);
};

const evaluateRule = (rule: ConditionRule, formData: FormData): boolean => {
    const field = rule.field ?? rule.key ?? '';
    const fieldValue = formData[field] ?? null;
    const targetValue = rule.value;

    switch (rule.comparison ?? '==') {
        case '!=':
            return !phpLooseEquals(fieldValue, targetValue);
        case '>':
            return Number(fieldValue) > Number(targetValue);
        case '>=':
            return Number(fieldValue) >= Number(targetValue);
        case '<':
            return Number(fieldValue) < Number(targetValue);
        case '<=':
            return Number(fieldValue) <= Number(targetValue);
        case 'in':
            return Array.isArray(targetValue)
                ? targetValue.some((candidate) => phpLooseEquals(fieldValue, candidate))
                : String(targetValue).includes(String(fieldValue));
        case 'notin':
        case 'not_in':
            return Array.isArray(targetValue)
                ? !targetValue.some((candidate) => phpLooseEquals(fieldValue, candidate))
                : !String(targetValue).includes(String(fieldValue));
        case 'empty':
            return phpEmpty(fieldValue);
        case 'not_empty':
            return !phpEmpty(fieldValue);
        case '==':
        default:
            return phpLooseEquals(fieldValue, targetValue);
    }
};

/**
 * Evaluate full conditional logic ({action, logic, rules}) or a legacy single
 * condition ({key|field, comparison, value}) against form data.
 */
export const shouldShow = (conditions: ConditionalLogic | ConditionRule, formData: FormData): boolean => {
    const isLegacySingle = !('rules' in conditions) && !('action' in conditions);

    const logic: ConditionalLogic = isLegacySingle
        ? { action: 'show', logic: 'all', rules: [conditions as ConditionRule] }
        : (conditions as ConditionalLogic);

    const rules = logic.rules ?? [];

    if (rules.length === 0) {
        return true;
    }

    const results = rules.map((rule) => evaluateRule(rule, formData));

    const matches = (logic.logic ?? 'all') === 'all' ? !results.includes(false) : results.includes(true);

    return (logic.action ?? 'show') === 'show' ? matches : !matches;
};

/**
 * Drop-in replacement for the historical per-repeater solveCondition(condition, row).
 */
export const solveCondition = (condition: ConditionRule | ConditionalLogic, row: FormData): boolean =>
    shouldShow(condition, row);
